import type { FC } from "react";

import type { UsageTotals } from "../../types/usage";

import {
  formatTokenCount,
  formatUsd,
  toFiniteNumber,
} from "../../utils/currency";
import {
  formatCacheHitRate,
  formatCostPerToken,
  formatPercentageChange,
  getCacheHitRate,
  getPercentageChange,
} from "../../utils/usageMetrics";
import { MetricCard } from "../common/MetricCard";

export interface UsageHeroMetricsProps {
  totals: UsageTotals | null;
  previousTotals?: UsageTotals | null;
}

/* ------------------------------------------------------------------ */
/*  TrendFooter – arrow + percentage change                            */
/* ------------------------------------------------------------------ */

function trendTextClass(isPositive: boolean, isNegative: boolean): string {
  if (isPositive) {
    return "text-emerald-600 dark:text-emerald-400";
  }
  if (isNegative) {
    return "text-rose-600 dark:text-rose-400";
  }
  return "text-gray-500 dark:text-gray-400";
}

const TrendFooter: FC<{ value: number | null; label: string }> = ({
  value,
  label,
}) => {
  if (value === null) {
    return null;
  }
  const isPositive = value > 0;
  const isNegative = value < 0;

  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      {isPositive && (
        <svg
          className="h-3.5 w-3.5 text-emerald-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 15l7-7 7 7"
          />
        </svg>
      )}
      {isNegative && (
        <svg
          className="h-3.5 w-3.5 text-rose-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      )}
      <span
        className={`text-xs font-medium ${trendTextClass(isPositive, isNegative)}`}
      >
        {formatPercentageChange(value)} {label}
      </span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  UsageHeroMetrics                                                   */
/* ------------------------------------------------------------------ */

export const UsageHeroMetrics: FC<UsageHeroMetricsProps> = ({
  totals,
  previousTotals,
}) => {
  const totalCost = toFiniteNumber(totals?.costUsd) ?? 0;
  const totalTokens = totals?.totalTokens ?? 0;
  const cachedInputTokens = totals?.cachedInputTokens ?? 0;
  const promptTokens = totals?.promptTokens ?? 0;

  const previousCost = toFiniteNumber(previousTotals?.costUsd);
  const previousTokens = previousTotals?.totalTokens ?? null;
  const previousCachedInputTokens = previousTotals?.cachedInputTokens ?? null;
  const previousPromptTokens = previousTotals?.promptTokens ?? null;

  const cacheHitRate = getCacheHitRate(cachedInputTokens, promptTokens);
  const previousCacheHitRate =
    previousCachedInputTokens !== null && previousPromptTokens !== null
      ? getCacheHitRate(previousCachedInputTokens, previousPromptTokens)
      : null;

  const costTrend =
    previousCost !== null ? getPercentageChange(totalCost, previousCost) : null;
  const tokensTrend =
    previousTokens !== null
      ? getPercentageChange(totalTokens, previousTokens)
      : null;
  const cacheRateTrend =
    cacheHitRate !== null && previousCacheHitRate !== null
      ? cacheHitRate - previousCacheHitRate
      : null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .672-3 1.5S10.343 11 12 11s3 .672 3 1.5S13.657 14 12 14m0-6V7m0 7v1m-6 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        }
        iconPosition="right"
        size="lg"
        label="Total Cost"
        value={totals ? formatUsd(totalCost) : "\u2014"}
        footer={
          previousTotals ? (
            <TrendFooter value={costTrend} label="vs prev period" />
          ) : undefined
        }
        variant="emerald"
      />

      <MetricCard
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        }
        iconPosition="right"
        size="lg"
        label="Total Tokens"
        value={totals ? formatTokenCount(totalTokens) : "\u2014"}
        footer={
          previousTotals ? (
            <TrendFooter value={tokensTrend} label="vs prev period" />
          ) : undefined
        }
        variant="indigo"
      />

      <MetricCard
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        }
        iconPosition="right"
        size="lg"
        label="Cost per Token"
        value={totals ? formatCostPerToken(totalCost, totalTokens) : "\u2014"}
        variant="violet"
      />

      <MetricCard
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
            />
          </svg>
        }
        iconPosition="right"
        size="lg"
        label="Cache Hit Rate"
        value={totals ? formatCacheHitRate(cacheHitRate) : "\u2014"}
        footer={
          previousTotals && cacheRateTrend !== null ? (
            <TrendFooter value={cacheRateTrend} label="pts vs prev" />
          ) : undefined
        }
        variant="amber"
      />
    </div>
  );
};
